import React, { type FC, useCallback, useRef } from 'react';
import { CreativeTypes } from '@bynder-studio/render-core';
import { Form } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import RenditionProfiles from './RenditionProfiles';
import { type RenditionConfiguration } from 'packages/services/renditionsService';

type FormatsProps = {
    creativeTypes: CreativeTypes[];
    onLoadRenditions: () => void;
    onSelectProfile: (profiles: Record<CreativeTypes, RenditionConfiguration>) => void;
    preferredConfigurationIdsByType: Record<CreativeTypes, number[] | undefined>;
};

const Formats: FC<FormatsProps> = ({
    creativeTypes,
    onLoadRenditions,
    onSelectProfile,
    preferredConfigurationIdsByType,
}) => {
    const { translate } = useTranslate();
    const loadedRenditionsByCreativeTypes = useRef<Record<CreativeTypes, boolean>>({
        [CreativeTypes.IMAGE]: false,
        [CreativeTypes.VIDEO]: false,
    });
    const selectedProfilesByCreativeTypes = useRef<Record<CreativeTypes, RenditionConfiguration>>({
        [CreativeTypes.IMAGE]: null,
        [CreativeTypes.VIDEO]: null,
    });

    const handleLoadRenditions = useCallback(
        (creativeType: CreativeTypes) => {
            loadedRenditionsByCreativeTypes.current[creativeType] = true;

            if (creativeTypes.every((creativeType) => loadedRenditionsByCreativeTypes.current[creativeType])) {
                onLoadRenditions();
            }
        },
        [creativeTypes, onLoadRenditions],
    );

    const handleSelectProfile = useCallback((creativeType: CreativeTypes, profile: RenditionConfiguration) => {
        selectedProfilesByCreativeTypes.current[creativeType] = profile;
        onSelectProfile({ ...selectedProfilesByCreativeTypes.current });
    }, []);

    return (
        <Form.Row>
            {creativeTypes.includes(CreativeTypes.IMAGE) && (
                <Form.Group>
                    <Form.Label>{translate('modal.bulk-export.format.image')}</Form.Label>
                    <RenditionProfiles
                        creativeType={CreativeTypes.IMAGE}
                        onLoadRenditions={handleLoadRenditions}
                        onSelectProfile={handleSelectProfile}
                        preferredConfigurationIds={preferredConfigurationIdsByType[CreativeTypes.IMAGE]}
                    />
                </Form.Group>
            )}
            {creativeTypes.includes(CreativeTypes.VIDEO) && (
                <Form.Group>
                    <Form.Label>{translate('modal.bulk-export.format.video')}</Form.Label>
                    <RenditionProfiles
                        creativeType={CreativeTypes.VIDEO}
                        onLoadRenditions={handleLoadRenditions}
                        onSelectProfile={handleSelectProfile}
                        preferredConfigurationIds={preferredConfigurationIdsByType[CreativeTypes.VIDEO]}
                    />
                </Form.Group>
            )}
        </Form.Row>
    );
};

export default Formats;
